<template>
    <b-row class="mt-4">
        <div class="position-fixed d-none d-lg-block col-2" style="z-index: 10">
            <b-list-group v-b-scrollspy class="table-of-contents">
                <b-list-group-item href="#overview">Overview</b-list-group-item>
                <b-list-group-item href="#getting-started">Getting Started</b-list-group-item>
                <b-list-group-item href="#mod-manifest">Mod Manifest</b-list-group-item>
                <b-list-group-item href="#mod-basics">Mod Basics</b-list-group-item>
            </b-list-group>
        </div>
        
        <div class="col-2 d-none d-lg-block"></div>
        <div class="col-12 col-lg-9 ml-3 guide-list">
            <h4 id="overview" class="mt-0">Overview</h4>
            <p>
                FarmD is a Unity game (built with version {{unityVersion}}) that supports user-created mods. Starting with FarmD v1.9.1, the game has a built-in mod manager and framework for creating
                and loading mods, as well as UI-building tools for mod configuration pages and controls.
            </p>

            <h4 id="getting-started">Getting Started</h4>
            <p>
                To start making your own mods, you'll need the following:
                <ul>
                    <li>FarmD itself, either installed from Steam or manually. You'll need to know where the game's files are in order to install mods as well as reference code libraries.</li>
                    <li>An IDE for editing code and building the .dll assembly. These guides use Visual Studio for instructions and examples.</li>
                    <li>.NET Framework 4.8.1, which may not be installed by default with your IDE. (<a href="https://dotnet.microsoft.com/en-us/download/dotnet-framework/net481">Download</a>)</li>
                </ul>

                The requirements for mods to be recognized and loaded by the game are for the files to be placed in the game's Mods folder, at (install location)/FarmD_Data/Mods/ - each mod should
                be placed in a separate subfolder within this Mods folder, and should contain the .dll library and a file named "manifest.json" which contains the mod's manifest (see
                <b-button variant="link" href="#mod-manifest">Mod Manifest</b-button>).
            </p>
            
            <h3>Create Project</h3>
            <div class="accordion" role="tablist">
                <b-card no-body class="mb-1">
                    <b-card-header header-tag="header" class="p-1" role="tab">
                        <b-button block v-b-toggle.accordion-1 variant="primary">Create project from FarmD Mod template</b-button>
                    </b-card-header>
                    <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
                        <b-card-body>
                            <p>
                                It is recommended to start with this basic template project to skip some of the initial setup steps: (<a href="/downloads/FarmDMod.zip">download</a>). Place this template in 
                                Visual Studio's Templates folder, which by default will be at C:/Users/(your username)/Documents/Visual Studio 20xx/Templates/ProjectTemplates/. The template should then be 
                                available for you when you create a new project (search for FarmD to filter the list easily).
                            </p>
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-1">
                    <b-card-header header-tag="header" class="p-1" role="tab">
                        <b-button block v-b-toggle.accordion-2 variant="primary">Create project using dotnet new</b-button>
                    </b-card-header>
                    <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                        <b-card-body>
                            <p>

                            </p>
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-1">
                    <b-card-header header-tag="header" class="p-1" role="tab">
                        <b-button block v-b-toggle.accordion-3 variant="primary">Create project from basic template</b-button>
                    </b-card-header>
                    <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
                        <b-card-body>
                            <p>
                                If not using the provided mod template, create a new project using the "Class Library (.NET Framework)" template.
                            </p>
                            <b-img src="/images/user-manual/modding/create-project.jpg" class="img-fluid mx-auto d-block"></b-img>
                            <p>
                                After naming your mod project and choosing the project directory, Visual Studio will launch. In the Solution Explorer, right-click on your project and select Properties, then
                                open the Build tab and enable the "Allow unsafe code" flag. This will prevent some edge-case exceptions from popping up in-game.
                            </p>
                            <b-img src="/images/user-manual/modding/build-settings.jpg" class="img-fluid mx-auto d-block"></b-img>
                            <p>
                                One other useful option to set in this Build tab is removing Debug symbols from your build, which can be accessed by clicking the "Advanced" button on the
                                Build page and setting the "Debugging information" option to "None".
                            </p>
                            <b-img src="/images/user-manual/modding/build-debug-symbols.jpg" class="img-fluid mx-auto d-block"></b-img>
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>

            <h3 class="mt-3">Add References</h3>
            <p>
                Now that your project has been created, one remaining step is to Add References to the FarmD assembly as well as any UnityEngine libraries
                that you will need, in order for the project to compile correctly. In the Solution Explorer, right-click on the References tab and click Add Reference.
            </p>
            <b-img src="/images/user-manual/modding/add-reference.jpg" class="img-fluid mx-auto d-block"></b-img>
            <p>
                In the Reference Manager window, click Browse in the lower-right and navigate to where the FarmD game files are installed on your computer. From that directory, go to 
                FarmD_Data/Managed/ and select the "Assembly-CSharp-publicized.dll" file. This is the game's main code library, with all class methods and fields publicized so they can 
                be referenced externally. You will also want to select UnityEngine.dll and UnityEngine.CoreModule.dll at minimum, and any other UnityEngine components depending on what 
                your mod will be interacting with.
            </p>

            <h4 id="mod-manifest">Mod Manifest</h4>
            <p>
                All mods must contain a file named "manifest.json" which contains details about the mod including its name and description for the Mods panel, as well as dependencies that need
                to be loaded in order for the mod to function. A sample manifest.json is included with the mod project template (<a href="/downloads/FarmDMod.zip">download</a>)
            </p>
            <p>
                <ul>
                    <li><span class="font-weight-bold">Internal Name:</span> This name is used for uniquely identifying the mod, and is used for things like generating the config-file
                    directories for saving mod settings.</li>
                    <li><span class="font-weight-bold">Display Name, Author, Description, Mod Version:</span> This information is displayed in the in-game Mods menu, and should be descriptive
                    to let players know what the mod is.</li>
                    <li><span class="font-weight-bold">Game Version:</span> The minimum version of the game required to run this mod. This should be in the format "x.y.z"</li>
                </ul>
            </p>

            <h4 id="mod-basics">Mod Basics</h4>
            <p>
                In order for your mod to be detected by the game's ModManager and show in the Mods panel of the Options menu, it must have one class that inherits from the FarmD.Modding.FDMod
                base class. This class contains hooks for initializing the mod early in the game's startup sequence as well as for building the mod's configuration page for the Mods panel.
            </p>
            <p>
                Mods can use UI builder tools (called FDUI) to construct a mod configuration page that is accessible from the Mods panel in the Options, to allow users to set properties of
                the mod or activate various functions. These settings are also stored in the user profile so they can be re-applied on subsequent play sessions.
            </p>
            <p>
                Once you have written the code for your mod, build the .dll library using the menu option Build -> Build Solution (or project). This will create a .dll file with the same name
                as your project in the project files, by default in the "bin/Debug" folder. Then simply place this .dll and the manifest.json in a folder and copy it to the game's files,
                in the "FarmD_Data/Mods" folder.
            </p>
            <p>
                For an example of a simple mod that uses the UI builder tools and saved settings, download the TestMod here: (<a href="/downloads/TestMod.zip">download</a>). This zip also
                contains the source code files used in the mod.
            </p>
        </div>
    </b-row>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: "ModdingQuickstart",
		computed: {
			...mapState(['unityVersion', 'dotNetVersion']),
        }
    }
</script>

<style>
    .accordion {
        border: 2px solid gray;
        border-radius: 5px;
    }

    .accordion .card {
        background-color: inherit;
    }

    .accordion .card-body {
        padding: 0.75rem;
    }

    p {
        margin-right: auto
    }

    .guide-list .card-header .btn {
        text-align: left;
        font-size: 1.2em;
        padding: 0.5rem;
    }
</style>